/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import {
	AuthResponse,
	AuthState,
	VerifyOtpResponse,
	authenticateUser,
	logout,
	refreshAccessToken,
	resendOtp,
	updateProfile,
	verifyOtp,
} from "../actions/auth"
import { changePassword } from "../actions/recover"

const initialState: AuthState = {
	user: undefined,
	loading: false,
	resendLoading: false,
	error: undefined,
	isOtpRequired: false,
	isAuthenticate: false,
}

const authSlice = createSlice({
	name: "auth",
	initialState: initialState,
	reducers: {
		setInitialState: (state) => {
			state.user = undefined
			state.loading = false
			state.resendLoading = false
			state.error = undefined
			state.isOtpRequired = false
			state.isAuthenticate = false
		},
		clearError: (state) => {
			state.error = undefined
			state.resendLoading = false
			state.loading = false
			state.user = undefined
		},
		clearOtpError: (state) => {
			state.error = undefined
		},
		clearOtpRequest: (state) => {
			state.error = undefined
			state.isOtpRequired = false
			state.resendLoading = false
			state.loading = false
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(authenticateUser.pending, (state) => {
				state.loading = true
				state.resendLoading = false
				state.isOtpRequired = false
				state.isAuthenticate = false
				state.error = undefined
			})
			.addCase(
				authenticateUser.fulfilled,
				(state: AuthState, action: PayloadAction<AuthResponse>) => {
					state.loading = false
					state.resendLoading = false
					state.isOtpRequired = true
					state.isAuthenticate = false
					state.error = undefined
					state.user = action?.payload
				}
			)
			.addCase(authenticateUser.rejected, (state: AuthState, action) => {
				state.loading = false
				state.resendLoading = false
				state.isOtpRequired = false
				state.isAuthenticate = false
				const errorMessage =
					typeof action.payload === "object" &&
					action.payload !== null &&
					"message" in action.payload
						? action.payload.message
						: typeof action.payload === "string"
							? action.payload
							: "generic"

				state.error = {
					message: errorMessage,
				}
			})
			.addCase(
				updateProfile.fulfilled,
				(state: AuthState, action: PayloadAction<AuthResponse>) => {
					state.user = {
						...state.user,
						avatar: action?.payload?.avatar,
						phone: action?.payload?.phone,
					}
				}
			)
			.addCase(
				changePassword.fulfilled,
				(state: AuthState, action: AuthResponse) => {
					state.isOtpRequired = false
					state.isAuthenticate = false
					state.user = {
						...state.user,
						resetToken:
							action?.payload?.message === "ok"
								? undefined
								: state.user?.resetToken,
						userPasswordReset:
							action?.payload?.message === "ok"
								? false
								: state.user?.userPasswordReset,
					}
				}
			)
			.addCase(verifyOtp.pending, (state: AuthState) => {
				state.loading = true
				state.resendLoading = false
				state.isAuthenticate = false
				state.error = undefined
			})
			.addCase(
				verifyOtp.fulfilled,
				(state: AuthState, action: PayloadAction<VerifyOtpResponse>) => {
					state.loading = false
					state.resendLoading = false
					state.isAuthenticate = !state.user?.userPasswordReset ? true : false
					state.error = undefined
					state.user = {
						...state.user,
						isAuthenticate: !state.user?.userPasswordReset ? true : false,
						permissions: action?.payload?.permissions,
						userCorporates: action?.payload?.userCorporates,
					}
					state.isOtpRequired = false
				}
			)
			.addCase(verifyOtp.rejected, (state: AuthState, action) => {
				state.loading = false
				state.resendLoading = false
				state.isAuthenticate = false
				const errorMessage =
					typeof action.payload === "object" &&
					action.payload !== null &&
					"message" in action.payload
						? action.payload.message
						: typeof action.payload === "string"
							? action.payload
							: "generic"

				state.error = {
					message: errorMessage,
				}
			})
			.addCase(resendOtp.pending, (state: AuthState) => {
				state.resendLoading = true
				state.isAuthenticate = false
				state.error = undefined
			})
			.addCase(resendOtp.fulfilled, (state: AuthState) => {
				state.resendLoading = false
				state.isAuthenticate = false
				state.isOtpRequired = true
				state.error = undefined
			})
			.addCase(resendOtp.rejected, (state: AuthState, action) => {
				state.resendLoading = false
				state.error = {
					message: action?.payload ?? "generic",
				}
			})
			.addCase(logout.pending, (state: AuthState) => {
				state.resendLoading = true
				state.isAuthenticate = false
				state.error = undefined
			})
			.addCase(logout.fulfilled, (state: AuthState) => {
				state.user = undefined
				state.resendLoading = false
				state.isOtpRequired = false
				state.isAuthenticate = false
				state.error = undefined
			})
			.addCase(logout.rejected, (state: AuthState) => {
				state.user = undefined
				state.resendLoading = false
				state.isOtpRequired = false
				state.isAuthenticate = false
				state.error = undefined
			})
			.addCase(refreshAccessToken.fulfilled, (state: AuthState, action) => {
				state.user = {
					...state.user,
					token: action?.payload,
				}
			})
			.addCase(refreshAccessToken.rejected, (state: AuthState) => {
				state.user = undefined
				state.resendLoading = false
				state.isOtpRequired = false
				state.isAuthenticate = false
				state.error = undefined
			})
	},
})

export const { clearError, clearOtpError, clearOtpRequest, setInitialState } =
	authSlice.actions

export default authSlice.reducer
